import rulePreview from '@/components/BuildersPages/PolicyBuilderPage/RulePreview/rulePreviewModule'
import auth from '@/vuex/authModule'
import home from '@/vuex/homeModule'
import policy from '@/vuex/policyModule'
import scriptAnalysis from '@/vuex/scriptAnalysisModule'
import search from '@/vuex/searchModule'
import { createDirectStore, defineActions, defineModule, defineMutations } from 'direct-vuex'
import Notify from 'quasar/src/plugins/Notify.js';
import Vue from 'vue'
import Vuex from 'vuex'
import appMeta from './appMetaModule'
import labels from './labelsModule'
import rule from './ruleModule'
import scriptKey from './scriptKeyModule'
import themes from './themesModule'
import userFunction from './userFunctionModule'

Vue.use(Vuex)

interface ShowNotificationPayload {
  message: string
  state?: 'negative' | 'positive' | 'info'
  icon?: string
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top' | 'bottom' | 'left' | 'right' | 'center'
}

interface ShowErrorPayload {
  handlerName: string
  error: any
}

let errorBuffer: any[] = []
let errorReportHandle: NodeJS.Timeout | undefined

interface RootState {
  hasErrors: boolean
}

const { store, rootActionContext, moduleActionContext } = createDirectStore({
  ...defineModule({
    state: {
      hasErrors: false,
    } as RootState,

    mutations: defineMutations<RootState>()({
      SET_HAS_ERRORS(state: RootState) {
        state.hasErrors = true
      },
    }),

    actions: defineActions({
      SHOW_NOTIFICATION(
        // eslint-disable-next-line no-unused-vars
        ctx,
        { message, state, icon, position }: ShowNotificationPayload
      ): void {
        Notify.create({
          message,
          color: state ? state : 'positive',
          icon: icon ? icon : 'cloud_done',
          position: position ? position : 'center',
          actions: [
            {
              icon: 'close',
              color: 'white',
              size: 'sm',
              handler: () => undefined,
            },
          ],
        })
      },

      SHOW_ERROR(
        // eslint-disable-next-line no-unused-vars
        ctx,
        { handlerName, error }: ShowErrorPayload
      ): void {
        rootActionContext(ctx).commit.SET_HAS_ERRORS()

        // eslint-disable-next-line no-console
        console.error(handlerName, error)

        if (!error) {
          // In some edge we get an undefined error.
          return
        }

        if (error.silent) {
          return
        }

        if (error.response && error.response.status === 404) {
          // We don't show user facing errors for 404s.
          // Views must have their own user informing components where applicable.
          return
        }

        errorBuffer.push(error)

        if (errorReportHandle) {
          return
        }

        errorReportHandle = setTimeout(() => {
          errorReportHandle = undefined

          const message =
            `Terribly sorry. We encountered the following error${errorBuffer.length > 1 ? 's' : ''}:<br><ul>` +
            errorBuffer
              .map(e => {
                if (e.response && e.response.data && e.response.data.tests_results) {
                  return `<li>${e.response.data.tests_results}</li>`
                } else if (e.response && e.response.data && e.response.data.message) {
                  return `<li>${e.response.data.message}</li>`
                } else {
                  return `<li>${e.message}</li>`
                }
              })
              .join('\n') +
            '</ul>'

          Notify.create({
            html: true,
            message,
            color: 'negative',
            icon: 'info',
            position: 'center',
            progress: true,
            multiLine: true,
            timeout: 10000,
            actions: [
              {
                label: 'Dismiss',
                color: 'white',
                handler: () => {
                  /* ... */
                },
              },
            ],
          })
          errorBuffer = []
        }, 1000)
      },
    }),
  }),

  modules: {
    auth,
    home,
    rulePreview,
    rule,
    policy,
    scriptKey,
    scriptAnalysis,
    search,
    appMeta,
    userFunction,
    labels,
    themes,
  },
})

export { moduleActionContext, rootActionContext, store }
export type AppStore = typeof store

export type AppState = typeof store.state

declare module 'vuex' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Store<S> {
    direct: AppStore
  }
}
